// src/pages/hr/recruitment/HiringRequests.jsx
import React from "react";
import { useRecruitmentContext } from "../../../context/RecruitmentContext";
import { approveRecruitmentRequest, rejectRecruitmentRequest, createJobOpening } from "../../../hooks/useRecruitment";
import { useAuth } from "../../../context/AuthContext";

export default function HiringRequests() {
  const { recruitmentRequests, requestsLoading } = useRecruitmentContext();
  const { userProfile } = useAuth();

  async function handleApprove(req) {
    if (!window.confirm(`Approve hiring request for "${req.positionTitle}"?`)) return;

    // Approve the request
    await approveRecruitmentRequest(req.id, userProfile?.uid, userProfile?.displayName || "HR Admin");

    // Auto-create Job Opening
    await createJobOpening({
      title: req.positionTitle,
      department: req.department,
      vacancies: Number(req.openings || 1),
      experienceRequired: req.experience,
      employmentType: req.employmentType || "Full-Time",
      priority: req.priority || "Medium",
      hiringManager: req.requestedByName || "Team Lead",
      description: req.justification || "",
      skillsRequired: req.skills || [],
      status: "Open",
      createdByName: userProfile?.displayName || "HR Admin",
    });

    alert("Hiring request approved and Job Opening created!");
  }

  async function handleReject(req) {
    const reason = window.prompt("Enter rejection reason:");
    if (reason === null) return;
    await rejectRecruitmentRequest(req.id, userProfile?.uid, userProfile?.displayName || "HR Admin", reason);
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "1.25rem" }}>
      <style>{`
        .req-card { background: var(--bg-card, #fff); border: 1px solid var(--border, #e5e7eb); border-radius: 12px; padding: 1.25rem; box-shadow: 0 1px 5px rgba(0,0,0,0.05); margin-bottom: 1rem; }
        .req-header { display: flex; justify-content: space-between; align-items: flex-start; gap: 0.5rem; }
        .req-title { font-size: 1.05rem; font-weight: 700; color: var(--text-primary, #111); }
        .req-meta { font-size: 0.78rem; color: var(--text-muted); margin-top: 4px; }
        .req-badge { padding: 0.2rem 0.65rem; border-radius: 99px; font-size: 0.72rem; font-weight: 700; }
        .req-badge--Submitted { background: #fef3c7; color: #92400e; }
        .req-badge--Approved { background: #d1fae5; color: #065f46; }
        .req-badge--Rejected { background: #fee2e2; color: #991b1b; }
      `}</style>

      <div>
        <h3 style={{ margin: 0, fontWeight: 700, fontSize: "1.05rem" }}>Team Lead Hiring Requisitions</h3>
        <span style={{ fontSize: "0.78rem", color: "var(--text-muted)" }}>
          Review headcount requests submitted by Team Leads
        </span>
      </div>

      {requestsLoading ? (
        <div style={{ textAlign: "center", padding: "3rem", color: "var(--text-muted)" }}>Loading Requests...</div>
      ) : recruitmentRequests.length === 0 ? (
        <div style={{ background: "var(--bg-card, #fff)", border: "1px dashed var(--border)", borderRadius: 12, padding: "3rem", textAlign: "center", color: "var(--text-muted)" }}>
          No hiring requisitions submitted.
        </div>
      ) : (
        <div>
          {recruitmentRequests.map((req) => (
            <div key={req.id} className="req-card">
              <div className="req-header">
                <div>
                  <div className="req-title">{req.positionTitle}</div>
                  <div className="req-meta">
                    Requested by <strong>{req.requestedByName}</strong> ({req.department}) · {req.openings} opening(s) · Priority: <strong>{req.priority || "Medium"}</strong>
                  </div>
                </div>
                <span className={`req-badge req-badge--${req.status}`}>{req.status}</span>
              </div>

              <div style={{ marginTop: "0.75rem", fontSize: "0.82rem", background: "var(--bg-secondary)", padding: "0.75rem", borderRadius: 8 }}>
                <div><strong>Justification:</strong> {req.justification || "None provided"}</div>
                {req.skills && (
                  <div style={{ marginTop: 4 }}>
                    <strong>Skills:</strong> {Array.isArray(req.skills) ? req.skills.join(", ") : req.skills}
                  </div>
                )}
                {req.salaryRange && <div style={{ marginTop: 4 }}><strong>Salary Range:</strong> {req.salaryRange}</div>}
              </div>

              {req.status === "Submitted" && (
                <div style={{ display: "flex", gap: "0.5rem", justifyContent: "flex-end", marginTop: "1rem" }}>
                  <button className="btn-secondary" style={{ color: "#ef4444" }} onClick={() => handleReject(req)}>
                    ✕ Reject
                  </button>
                  <button className="btn-primary" onClick={() => handleApprove(req)}>
                    ✓ Approve & Create Job Opening
                  </button>
                </div>
              )}

              {req.status === "Approved" && (
                <div style={{ marginTop: "0.75rem", fontSize: "0.75rem", color: "#10b981", fontWeight: 600 }}>
                  Approved by {req.approvedByName}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
